import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RANGES = [{ d: 1, l: '24h' }, { d: 7, l: '7d' }, { d: 30, l: '30d' }, { d: 90, l: '90d' }];
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Kpi = ({ label, value, sub, color, testid }) => (
    <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wide text-[#A0AAB2]">{label}</div>
        <div className="font-mono font-black text-xl mt-1" style={{ color: color || '#fff' }}>{value}</div>
        {sub != null && <div className="text-[10px] text-[#A0AAB2] mt-0.5">{sub}</div>}
    </div>
);

export const BofReportsAdminTab = () => {
    const [days, setDays] = useState(30);
    const [rep, setRep] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/bof/report?days=${d}`, { withCredentials: true });
            setRep(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Balls of Fire report');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(days); }, [days, load]);

    const rtpOk = rep && rep.handle > 0 && Math.abs(rep.rtp_pct - rep.target_rtp_pct) <= 6;
    const ggrPos = rep && rep.ggr >= 0;

    return (
        <div className="card-dark p-5 space-y-4" data-testid="bof-reports-admin">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-[#ff8a1e]" />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">Balls of Fire — Reports</h3>
                </div>
                <div className="flex items-center gap-1">
                    {RANGES.map((r) => (
                        <Button key={r.d} size="sm" onClick={() => setDays(r.d)}
                            variant={days === r.d ? 'default' : 'ghost'}
                            className={`h-8 text-xs ${days === r.d ? 'bg-[#ff8a1e] text-black hover:bg-[#ff8a1e]/90' : 'text-[#A0AAB2]'}`}
                            data-testid={`bof-report-range-${r.d}`}>{r.l}</Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => load(days)} className="text-[#A0AAB2] h-8 text-xs" data-testid="bof-report-refresh">
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>
            <p className="text-[11px] text-[#A0AAB2]">
                Real financials from the transaction ledger over the selected window. Handle = total wagered, GGR = house gross win (handle − wins).
            </p>

            {!rep ? (
                <div className="text-[#A0AAB2] py-12 text-center">Loading…</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Kpi label="Handle (wagered)" value={usd(rep.handle)} sub={`${rep.spins.toLocaleString()} spins`} color="#ffd24a" testid="bof-kpi-handle" />
                        <Kpi label="Total Wins" value={usd(rep.wins)} color="#3aa0ff" testid="bof-kpi-wins" />
                        <Kpi label="GGR (house)" value={usd(rep.ggr)} color={ggrPos ? '#3ad07a' : '#ff5a5a'} testid="bof-kpi-ggr" />
                        <Kpi label="Actual RTP" value={`${rep.rtp_pct}%`} sub={`target ${rep.target_rtp_pct}%`} color={rtpOk ? '#3ad07a' : '#ffb020'} testid="bof-kpi-rtp" />
                        <Kpi label="Hold %" value={`${rep.hold_pct}%`} color={ggrPos ? '#3ad07a' : '#ff5a5a'} testid="bof-kpi-hold" />
                        <Kpi label="Unique Players" value={rep.unique_players.toLocaleString()} testid="bof-kpi-players" />
                        <Kpi label="Avg Bet" value={usd(rep.avg_bet)} testid="bof-kpi-avgbet" />
                        <Kpi label="Jackpot Payouts" value={usd(rep.jackpot_payouts)} sub="pool prizes (excl. from cap)" color="#b06bff" testid="bof-kpi-jackpots" />
                    </div>

                    {rep.handle > 0 && !rtpOk && (
                        <div className="text-[11px] text-[#ffb020] flex items-center gap-1">
                            {rep.rtp_pct > rep.target_rtp_pct ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            Actual RTP is {Math.abs(rep.rtp_pct - rep.target_rtp_pct).toFixed(1)}pts from target — normal for small samples; converges to {rep.target_rtp_pct}% over volume.
                        </div>
                    )}

                    {Object.keys(rep.jackpots_by_tier || {}).length > 0 && (
                        <div className="pt-2 border-t border-white/5">
                            <div className="text-[10px] uppercase tracking-wide text-[#A0AAB2] mb-2">Jackpot hits by tier</div>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(rep.jackpots_by_tier).map(([t, v]) => (
                                    <div key={t} className="px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.02] text-xs" data-testid={`bof-jp-tier-${t.toLowerCase()}`}>
                                        <span className="font-black text-white">{t}</span>
                                        <span className="text-[#A0AAB2] ml-2">{v.count}× · {usd(v.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {(rep.trend || []).length > 0 && (
                        <div className="pt-2 border-t border-white/5">
                            <div className="text-[10px] uppercase tracking-wide text-[#A0AAB2] mb-2">Daily trend</div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid="bof-report-trend">
                                    <thead>
                                        <tr className="text-[#A0AAB2] text-left">
                                            <th className="py-1 pr-4 font-normal">Date</th>
                                            <th className="py-1 pr-4 font-normal text-right">Handle</th>
                                            <th className="py-1 pr-4 font-normal text-right">Wins</th>
                                            <th className="py-1 font-normal text-right">GGR</th>
                                        </tr>
                                    </thead>
                                    <tbody className="font-mono">
                                        {rep.trend.slice(-14).reverse().map((row) => (
                                            <tr key={row.date} className="border-t border-white/5">
                                                <td className="py-1 pr-4 text-white">{row.date}</td>
                                                <td className="py-1 pr-4 text-right text-[#ffd24a]">{usd(row.handle)}</td>
                                                <td className="py-1 pr-4 text-right text-[#3aa0ff]">{usd(row.wins)}</td>
                                                <td className={`py-1 text-right ${row.ggr >= 0 ? 'text-[#3ad07a]' : 'text-[#ff5a5a]'}`}>{usd(row.ggr)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default BofReportsAdminTab;
